import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, TrendingUp, Wrench, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getDeckScore, claimDeck, getDeckFileUrl, getDeckStatus, scoreDeck } from "@/lib/deck.functions";
import { posthog } from "@/lib/posthog";
import { Sentry } from "@/lib/sentry";
import { IkomatchCTA } from "@/components/ikomatch-cta";
import { usePricing } from "@/lib/pricing";


type ResultsSearch = { deck?: string };

export const Route = createFileRoute("/results")({
  validateSearch: (search: Record<string, unknown>): ResultsSearch => ({
    deck: typeof search.deck === "string" ? search.deck : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Your deck analysis - DeckScore" },
      {
        name: "description",
        content:
          "See where investors will push back on your pitch deck. Free 3-slide preview, unlock the rest with DeckScore.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResultsPage,
});

type SlideStatus = "green" | "amber" | "red";
type SubScores = {
  clarity: number;
  substance: number;
  relevance: number;
  credibility: number;
  design: number;
};
type Slide = {
  n: number;
  title: string;
  status: SlideStatus;
  score: number;
  evidence: string;
  strength: string;
  fix: string;
  rewriteHeadline: string;
  rewriteBody: string[];
  subScores: SubScores;
  locked: boolean;
};
type TopFix = { rank: number; slide: string; fix: string };
type ResultsView = {
  score: number;
  slidesScanned: number;
  criticalIssues: number;
  verdict: string;
  topFixes: TopFix[];
  slides: Slide[];
  weakEssentials: string[];
};

const MOCK: ResultsView = {
  score: 61,
  slidesScanned: 10,
  criticalIssues: 3,
  verdict: "Investable story, weak market proof - fix slides 3, 4 and 8 first.",
  topFixes: [
    { rank: 1, slide: "SLIDE 2 - PROBLEM", fix: "Quantify the pain - one stat beats three adjectives." },
    { rank: 2, slide: "SLIDE 3 - SOLUTION", fix: "Leads with features, not the outcome investors buy." },
    { rank: 3, slide: "SLIDE 4 - MARKET SIZE", fix: "TAM math isn't shown - investors will ask for the build-up." },
  ],
  slides: [
    { n: 1, title: "Cover", status: "green", score: 9, evidence: "Logo, one-liner, contact all present.", strength: "Clear one-liner, logo, contact - nothing to change.", fix: "", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 2, substance: 2, relevance: 2, credibility: 2, design: 1 }, locked: false },
    { n: 2, title: "Problem", status: "amber", score: 6, evidence: "Adjective-heavy pain description.", strength: "Right problem picked.", fix: "Quantify the pain - one stat beats three adjectives.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 1, relevance: 2, credibility: 1, design: 1 }, locked: false },
    { n: 3, title: "Solution", status: "red", score: 4, evidence: "Feature list, no outcome.", strength: "Product is real.", fix: "Leads with features, not the outcome investors buy.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 1, relevance: 1, credibility: 0, design: 1 }, locked: false },
    { n: 4, title: "Market size", status: "red", score: 3, evidence: "TAM number with no source.", strength: "Big market claimed.", fix: "TAM math isn't shown - investors will ask for the build-up.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 0, relevance: 1, credibility: 0, design: 1 }, locked: true },
    { n: 5, title: "Product", status: "amber", score: 6, evidence: "Dense screenshot.", strength: "Real UI shown.", fix: "Screenshot is dense - annotate the one thing to notice.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 1, relevance: 2, credibility: 1, design: 1 }, locked: true },
    { n: 6, title: "Traction", status: "amber", score: 6, evidence: "Growth chart present.", strength: "Growth is up-and-to-the-right.", fix: "Growth chart has no axis labels or time frame.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 1, relevance: 2, credibility: 1, design: 1 }, locked: true },
    { n: 7, title: "Business model", status: "green", score: 8, evidence: "Pricing tiers listed.", strength: "Pricing logic is clear and defensible.", fix: "", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 2, substance: 2, relevance: 2, credibility: 1, design: 1 }, locked: true },
    { n: 8, title: "Competition", status: "red", score: 3, evidence: "2x2 with no incumbent.", strength: "Framework attempted.", fix: "2x2 omits the obvious incumbent - looks evasive.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 0, relevance: 1, credibility: 0, design: 1 }, locked: true },
    { n: 9, title: "Team", status: "green", score: 9, evidence: "Founders + relevant roles.", strength: "Relevant backgrounds, well framed.", fix: "", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 2, substance: 2, relevance: 2, credibility: 2, design: 1 }, locked: true },
    { n: 10, title: "The ask", status: "amber", score: 6, evidence: "Ask amount stated.", strength: "Ask is specific.", fix: "Use of funds is vague - tie it to a milestone.", rewriteHeadline: "", rewriteBody: [], subScores: { clarity: 1, substance: 1, relevance: 2, credibility: 1, design: 1 }, locked: true },
  ],
  weakEssentials: [],
};

const STATUS_COLOR: Record<SlideStatus, string> = {
  green: "#5FAE7C",
  amber: "#C99A3E",
  red: "#C65C4E",
};

function mapScoreRow(row: any): ResultsView | null {
  if (!row) return null;
  const analysis = row.analysis ?? {};
  const analysisSlides: any[] = Array.isArray(analysis?.slides) ? analysis.slides : [];
  const flags: any[] = Array.isArray(row.slide_flags) ? row.slide_flags : [];
  const fixes: string[] = Array.isArray(row.fixes) ? row.fixes : [];
  const topFixesRaw: any[] = Array.isArray(analysis?.top_fixes) ? analysis.top_fixes : [];

  const slides: Slide[] = flags.map((f, i) => {
    const src = analysisSlides[i] ?? {};
    const sc = src?.scores ?? {};
    const body = Array.isArray(src?.suggested_rewrite?.body)
      ? src.suggested_rewrite.body.map((b: any) => String(b))
      : [];
    return {
      n: Number(src?.n ?? i + 1),
      title: String(f?.slide ?? src?.title ?? `Slide ${i + 1}`),
      status: (f?.status ?? "amber") as SlideStatus,
      score: Number(src?.slide_score ?? 0),
      evidence: String(src?.evidence ?? ""),
      strength: String(src?.strength ?? ""),
      fix: String(src?.fix ?? f?.note ?? ""),
      rewriteHeadline: String(src?.suggested_rewrite?.headline ?? ""),
      rewriteBody: body,
      subScores: {
        clarity: Number(sc?.clarity ?? 0),
        substance: Number(sc?.substance ?? 0),
        relevance: Number(sc?.relevance ?? 0),
        credibility: Number(sc?.credibility ?? 0),
        design: Number(sc?.design ?? 0),
      },
      locked: i >= 3,
    };
  });

  const topFixes: TopFix[] = (topFixesRaw.length > 0
    ? topFixesRaw.slice(0, 3).map((f: any, i: number) => ({
        rank: Number(f?.rank ?? i + 1),
        slide: String(f?.issue ?? "").toUpperCase(),
        fix: String(f?.how ?? f?.why_it_matters ?? ""),
      }))
    : fixes.slice(0, 3).map((s, i) => ({ rank: i + 1, slide: `FIX ${i + 1}`, fix: s })));

  const criticalIssues = slides.filter((s) => s.status === "red").length;

  const essentialsArr: any[] = Array.isArray(analysis?.essentials) ? analysis.essentials : [];
  const ESSENTIAL_W: Record<string, number> = {
    problem: 10, solution: 12, market: 10, traction: 18, business_model: 12,
    gtm: 8, competition: 8, team: 12, ask: 6, financials: 4,
  };
  const weakEssentials: string[] = essentialsArr
    .filter((e) => Number(e?.strength ?? 0) <= 4)
    .sort((a, b) => (ESSENTIAL_W[String(b?.id ?? "")] ?? 0) - (ESSENTIAL_W[String(a?.id ?? "")] ?? 0))
    .map((e) => String(e?.element ?? e?.id ?? "").trim())
    .filter(Boolean)
    .slice(0, 4);

  return {
    score: Math.round(Number(row.overall_score ?? 0)),
    slidesScanned: slides.length || Number(analysis?.deck_meta?.slides_detected ?? 0),
    criticalIssues,
    verdict: String(row.verdict ?? analysis?.investor_readiness?.one_line_verdict ?? ""),
    topFixes,
    slides,
    weakEssentials,
  };
}

function ResultsPage() {
  const { deck: deckId } = Route.useSearch();
  const pricing = usePricing();
  const fetchScore = useServerFn(getDeckScore);
  const fetchDeckUrl = useServerFn(getDeckFileUrl);
  const fetchStatus = useServerFn(getDeckStatus);
  const runScoreDeck = useServerFn(scoreDeck);
  const runClaimDeck = useServerFn(claimDeck);

  // Poll deck status every 2.5s until scored/failed.
  const { data: statusRow } = useQuery({
    queryKey: ["deck-status", deckId],
    queryFn: () => fetchStatus({ data: { deckId: deckId! } }),
    enabled: !!deckId,
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string | null } | undefined)?.status;
      return s === "scored" || s === "failed" ? false : 2500;
    },
  });
  const deckStatus = statusRow?.status ?? null;

  const { data: scoreRow, isLoading: scoreLoading, isError: scoreError } = useQuery({
    queryKey: ["deck-score", deckId, deckStatus],
    queryFn: () => fetchScore({ data: { deckId: deckId! } }),
    enabled: !!deckId && (deckStatus === "scored" || deckStatus === null),
    staleTime: 60_000,
    refetchInterval: (q) => {
      const row = q.state.data as { overall_score?: number } | null | undefined;
      if (row?.overall_score != null) return false;
      if (deckStatus === "scored") return 2000;
      return false;
    },
  });
  const { data: fileUrlRow } = useQuery({
    queryKey: ["deck-file-url", deckId],
    queryFn: () => fetchDeckUrl({ data: { deckId: deckId! } }),
    enabled: !!deckId,
    staleTime: 5 * 60_000,
  });
  const pdfUrl = fileUrlRow?.url ?? null;

  // Kick off background scoring on mount (idempotent server-side).
  const scoreTriggeredRef = useRef(false);
  useEffect(() => {
    if (!deckId || scoreTriggeredRef.current) return;
    if (deckStatus === "scored" || deckStatus === "failed") return;
    scoreTriggeredRef.current = true;
    runScoreDeck({ data: { deckId } }).catch(() => {});
  }, [deckId, deckStatus, runScoreDeck]);

  // Safety net: if still processing after 4 minutes, re-trigger scoring once.
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const retriggeredRef = useRef(false);
  useEffect(() => {
    if (!deckId) return;
    if (deckStatus === "scored" || deckStatus === "failed") return;
    const t = setTimeout(() => {
      setPollTimedOut(true);
      if (!retriggeredRef.current) {
        retriggeredRef.current = true;
        runScoreDeck({ data: { deckId } }).catch(() => {});
      }
    }, 4 * 60_000);
    return () => clearTimeout(t);
  }, [deckId, deckStatus, runScoreDeck]);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const claimedRef = useRef(false);
  const [thumbnails, setThumbnails] = useState<Record<number, string>>({});
  const pdfDocRef = useRef<any>(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setIsLoggedIn(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !deckId || claimedRef.current) return;
    claimedRef.current = true;
    runClaimDeck({ data: { deckId } }).catch(() => {});
  }, [isLoggedIn, deckId, runClaimDeck]);

  useEffect(() => {
    if (isLoggedIn || !deckId) return;
    try { localStorage.setItem("deckscore_pending_deck", deckId); } catch { /* ignore */ }
  }, [isLoggedIn, deckId]);
  const scoredRef = useRef(false);
  useEffect(() => {
    if (scoredRef.current || !deckId || !scoreRow) return;
    scoredRef.current = true;
    try {
      const analysis = (scoreRow as any)?.analysis ?? {};
      const numSlides = Array.isArray((scoreRow as any)?.slide_flags)
        ? (scoreRow as any).slide_flags.length
        : Number(analysis?.deck_meta?.slides_detected ?? 0);
      const overall = Number((scoreRow as any)?.overall_score ?? 0);
      posthog.capture("deck_scored", {
        deck_id: deckId,
        overall_score: overall,
        num_slides: numSlides,
      });
      try {
        Sentry.setTag("deck_id", deckId);
        Sentry.setContext("score", { overall_score: overall, num_slides: numSlides });
      } catch { /* non-fatal */ }
    } catch { /* non-fatal */ }
  }, [deckId, scoreRow]);


  useEffect(() => {
    if (typeof window === "undefined" || !pdfUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs: any = await import("pdfjs-dist");
        if (!pdfjs.GlobalWorkerOptions.workerSrc) {
          const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
          pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        }
        const doc = await pdfjs.getDocument({ url: pdfUrl }).promise;
        pdfDocRef.current = doc;
        const pending: Record<number, string> = {};
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return;
          const page = await doc.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const scale = 640 / base.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (n === 1) console.log("[thumb] rendered page 1 ok");
          pending[n] = canvas.toDataURL("image/jpeg", 0.85);
          if (n % 3 === 0 || n === doc.numPages) {
            const snapshot = { ...pending };
            if (!cancelled) setThumbnails((prev) => ({ ...prev, ...snapshot }));
          }
        }
      } catch (err) {
        console.error("[thumb] FAILED:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  const mapped = mapScoreRow(scoreRow);
  const isDev = import.meta.env.DEV;
  const isProcessing = !!deckId && !mapped && (deckStatus === "processing" || deckStatus === "uploaded" || deckStatus === null || scoreLoading);
  const isFailed = !mapped && deckStatus === "failed";
  const showLoading = isProcessing && !isFailed;
  const showEmpty = !mapped && !showLoading && !isFailed && !isDev;
  const view = mapped ?? MOCK;
  const { score, slidesScanned, criticalIssues, verdict, topFixes, weakEssentials } = view;
  const slides = isLoggedIn ? view.slides.map((s) => ({ ...s, locked: false })) : view.slides;
  const gaugeDeg = score * 3.6;

  const loginSearch = { deck: deckId, next: "/results" };

  // Lightbox state
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);

  const unlockedIndexes = slides.reduce<number[]>((acc, s, i) => {
    if (!s.locked) acc.push(i);
    return acc;
  }, []);
  const currentUnlockedPos =
    lightboxIdx === null ? -1 : unlockedIndexes.indexOf(lightboxIdx);
  const hasPrev = currentUnlockedPos > 0;
  const hasNext = currentUnlockedPos >= 0 && currentUnlockedPos < unlockedIndexes.length - 1;

  const openLightbox = useCallback((i: number) => setLightboxIdx(i), []);
  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
    setLightboxImg(null);
  }, []);
  const gotoPrev = useCallback(() => {
    if (currentUnlockedPos > 0) setLightboxIdx(unlockedIndexes[currentUnlockedPos - 1]);
  }, [currentUnlockedPos, unlockedIndexes]);
  const gotoNext = useCallback(() => {
    if (currentUnlockedPos >= 0 && currentUnlockedPos < unlockedIndexes.length - 1) {
      setLightboxIdx(unlockedIndexes[currentUnlockedPos + 1]);
    }
  }, [currentUnlockedPos, unlockedIndexes]);

  // Render big image for the active lightbox slide on demand
  useEffect(() => {
    if (lightboxIdx === null) return;
    const slide = slides[lightboxIdx];
    if (!slide) return;
    const doc = pdfDocRef.current;
    if (!doc) {
      setLightboxImg(thumbnails[slide.n] ?? null);
      return;
    }
    let cancelled = false;
    setLightboxLoading(true);
    setLightboxImg(thumbnails[slide.n] ?? null);
    (async () => {
      try {
        const page = await doc.getPage(slide.n);
        const base = page.getViewport({ scale: 1 });
        const scale = 1100 / base.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        setLightboxImg(canvas.toDataURL("image/jpeg", 0.9));
      } catch {
        // fall back to thumbnail
      } finally {
        if (!cancelled) setLightboxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lightboxIdx, slides, thumbnails]);

  // Keyboard nav
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") gotoPrev();
      else if (e.key === "ArrowRight") gotoNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, closeLightbox, gotoPrev, gotoNext]);

  const activeSlide = lightboxIdx !== null ? slides[lightboxIdx] : null;







  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-line bg-bg">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block h-[18px] w-[18px] rounded-md bg-accent" />
            <span className="font-display text-[15px] font-semibold">DeckScore</span>
          </Link>
          <nav className="flex items-center gap-5">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="rounded-full border border-line px-3.5 py-1.5 text-[13px] text-ink hover:border-ink transition"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                search={loginSearch}
                className="rounded-full border border-line px-3.5 py-1.5 text-[13px] text-ink hover:border-ink transition"
              >
                Sign in
              </Link>
            )}
          </nav>

        </div>
      </header>

      {showLoading ? (
        <main className="flex-1">
          <div className="max-w-[720px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
            <p className="font-script text-accent text-[22px] leading-none">please wait</p>
            <h1 className="font-display text-[28px] md:text-[36px] font-semibold mt-2 mb-4">Analyzing your deck…</h1>
            <p className="text-ink-secondary text-[15px]">
              {pollTimedOut
                ? "This is taking longer than expected. We've retried once - if it doesn't finish in a minute, refresh this page."
                : "This usually takes 20-60 seconds. Stay on this page."}
            </p>
            <div className="mt-5 flex flex-col items-center gap-2">
              <p className="text-[13px] text-ink-tertiary">Deck already strong?</p>
              <IkomatchCTA
                placement="wait_state"
                variant="unstyled"
                className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-line bg-surface px-5 py-2 text-[13.5px] font-semibold text-ink hover:bg-bg transition"
              >
                Start free investor outreach →
              </IkomatchCTA>
            </div>
            <div className="mt-10 grid gap-3">
              <div className="h-24 rounded-2xl bg-surface border border-line animate-pulse" />
              <div className="h-24 rounded-2xl bg-surface border border-line animate-pulse" />
              <div className="h-24 rounded-2xl bg-surface border border-line animate-pulse" />
            </div>
          </div>
        </main>
      ) : isFailed ? (
        <main className="flex-1">
          <div className="max-w-[640px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
            <h1 className="font-display text-[28px] md:text-[36px] font-semibold mb-3">We couldn't score this deck</h1>
            <p className="text-ink-secondary text-[15px] mb-8">
              Please try again in a minute, or upload a different PDF.
            </p>
            <Link
              to="/upload"
              className="inline-flex items-center justify-center rounded-full bg-accent px-6 py-3 text-[14px] font-bold text-accent-ink hover:opacity-90 transition"
            >
              Upload a deck
            </Link>
          </div>
        </main>
      ) : showEmpty ? (
        <main className="flex-1">
          <div className="max-w-[640px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
            <h1 className="font-display text-[28px] md:text-[36px] font-semibold mb-3">We couldn't load this analysis</h1>
            <p className="text-ink-secondary text-[15px] mb-8">
              {scoreError ? "Something went wrong loading your score. " : ""}Re-upload your deck or try again.
            </p>
            <Link
              to="/upload"
              className="inline-flex items-center justify-center rounded-full bg-accent px-6 py-3 text-[14px] font-bold text-accent-ink hover:opacity-90 transition"
            >
              Upload a deck
            </Link>
          </div>
        </main>
      ) : (
      <main className="flex-1">

        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-8 md:py-11">
          <p className="font-script text-accent text-[22px] leading-none">deck analysis complete</p>
          <h1 className="font-display text-[28px] md:text-[36px] font-semibold max-w-[640px] mb-10 mt-2">
            Here's where investors will push back.
          </h1>

          {/* Hero grid */}
          <section className="grid md:grid-cols-[0.85fr_1.15fr] gap-4 bg-peach rounded-3xl p-6 md:p-8 mb-10">
            {/* Score card */}
            <div className="bg-surface border border-line rounded-2xl p-8 flex flex-col items-center text-center">
              <div
                role="img"
                aria-label={`Investor-readiness score ${score} out of 100`}
                className="relative rounded-full h-[140px] w-[140px] md:h-[168px] md:w-[168px] grid place-items-center"
                style={{
                  background: `conic-gradient(var(--color-accent) ${gaugeDeg}deg, var(--color-surface-sunken) ${gaugeDeg}deg)`,
                }}
              >
                <div className="bg-surface rounded-full h-[calc(100%-16px)] w-[calc(100%-16px)] flex flex-col items-center justify-center">
                  <span className="font-display text-[44px] font-semibold leading-none">{score}</span>
                  <span className="font-mono text-[13px] text-ink-tertiary mt-1">/ 100</span>
                </div>
              </div>
              <p className="text-ink-secondary text-[13px] mt-5">Investor-readiness score</p>
              <p className="text-ink text-[13.5px] max-w-[280px] mt-2">{verdict}</p>
              <p className="text-[11.5px] text-ink-tertiary mt-2 max-w-[280px] leading-snug">
                Measures fundraising fundamentals - problem, market, traction, business model, team &amp; ask. Not slide design.
              </p>
              {score < 55 && weakEssentials.length > 0 && (
                <p className="text-[12px] text-ink-secondary mt-3 max-w-[280px] leading-snug rounded-lg bg-surface-sunken border border-line px-3 py-2">
                  Polished slides can still score low here. Yours is dragged down by weak or missing{" "}
                  <span className="text-ink font-medium">{weakEssentials.join(", ")}</span> - that's content investors screen for, not slide design.
                </p>
              )}
              <div className="mt-3.5 pt-3.5 border-t border-line w-full text-[12px] text-ink-tertiary text-center">
                <span className="font-mono font-semibold text-ink">{slidesScanned}</span> slides scanned
                <span className="mx-2">•</span>
                <span className="font-mono font-semibold text-ink">{criticalIssues}</span> critical issues
              </div>
            </div>


            {/* Top 3 fixes */}
            <div className="relative bg-surface border border-line rounded-2xl p-6 flex flex-col gap-3 overflow-hidden">
              <h2 className="font-display text-[15px] font-semibold">Top 3 fixes before you send this</h2>
              <div className="flex flex-col gap-3">
                {topFixes.map((f, idx) => {
                  const gated = !isLoggedIn && idx > 0;
                  return (
                    <div
                      key={f.rank}
                      className={`relative border-t border-line pt-3 flex gap-3.5 ${gated ? "overflow-hidden" : ""}`}
                    >
                      <span
                        className="h-[22px] w-[22px] shrink-0 rounded-full grid place-items-center font-mono text-[12px] text-accent"
                        style={{ backgroundColor: "color-mix(in oklab, var(--color-accent) 15%, transparent)" }}
                      >
                        {f.rank}
                      </span>
                      <div
                        aria-hidden={gated}
                        className={`min-w-0 ${gated ? "pointer-events-none select-none" : ""}`}
                        style={gated ? { filter: "blur(6px)", WebkitUserSelect: "none" } : undefined}
                      >
                        <p className="uppercase text-[12px] font-semibold text-ink-tertiary tracking-wide">{f.slide}</p>
                        <p className="text-[13.5px] text-ink mt-0.5">{f.fix}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!isLoggedIn && topFixes.length > 1 && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center justify-end gap-2.5 rounded-b-2xl pb-5 pt-10 bg-gradient-to-b from-transparent via-surface/40 to-surface/70">
                  <Link
                    to="/login"
                    search={loginSearch}
                    className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-ink bg-ink px-4 py-2 text-[13px] font-medium text-sky hover:opacity-90 transition"
                  >
                    <Lock className="h-3.5 w-3.5" />
                    Sign in to unlock the other {topFixes.length - 1} {topFixes.length - 1 === 1 ? "fix" : "fixes"}
                  </Link>
                </div>
              )}
            </div>
          </section>


          {/* Breakdown header */}
          <div className="mb-4">
            <div className="flex justify-between items-baseline flex-wrap gap-2">
              <h2 className="font-display text-[19px] font-semibold">Slide-by-slide breakdown</h2>
              <p className="text-ink-tertiary text-[13px]">
                Free preview covers your first 3 slides · <span className="font-mono text-ink-secondary">{slides.length}</span> slides analyzed
              </p>
            </div>
            <p className="text-ink-tertiary text-[12.5px] mt-1">Each slide is scored on content &amp; design only - separate from the overall readiness score.</p>
          </div>

          {/* Bento */}
          <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
            {slides.map((s, i) => {
              const color = STATUS_COLOR[s.status];
              const thumb = thumbnails[s.n];
              return (
                <div
                  key={`${s.n}-${i}`}
                  className={
                    "relative bg-surface border border-line rounded-2xl p-3 flex flex-col gap-3 overflow-hidden animate-in fade-in duration-500 transition " +
                    (s.locked
                      ? ""
                      : "cursor-pointer hover:border-ink/40 hover:shadow-sm")
                  }
                  style={{ animationDelay: `${Math.min(i, 20) * 30}ms`, animationFillMode: "backwards" }}
                  onClick={s.locked ? undefined : () => openLightbox(i)}
                  role={s.locked ? undefined : "button"}
                  tabIndex={s.locked ? undefined : 0}
                  onKeyDown={
                    s.locked
                      ? undefined
                      : (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openLightbox(i);
                          }
                        }
                  }
                >
                  {/* Thumbnail - always visible */}
                  <div className="relative w-full aspect-[16/10] rounded-lg border border-line overflow-hidden bg-surface-sunken">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={`Slide ${s.n} thumbnail`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full animate-pulse bg-gradient-to-br from-surface-sunken to-line" />
                    )}
                  </div>

                  {/* Analysis block - blur + gated for locked */}
                  <div className="relative px-2 pb-2">
                    <div
                      aria-hidden={s.locked ? true : undefined}
                      className={
                        s.locked
                          ? "flex flex-col gap-2.5 pointer-events-none select-none"
                          : "flex flex-col gap-2.5"
                      }
                      style={s.locked ? { filter: "blur(12px)", WebkitUserSelect: "none" } : undefined}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full inline-block"
                            style={{ backgroundColor: color }}
                          />
                          <span className="font-mono text-[12px] text-ink-tertiary">{String(s.n).padStart(2, "0")}</span>
                        </div>
                        <span
                          className="font-mono text-[11.5px] px-2 py-0.5 rounded-full border"
                          style={{
                            color,
                            borderColor: `color-mix(in oklab, ${color} 40%, transparent)`,
                            backgroundColor: `color-mix(in oklab, ${color} 12%, transparent)`,
                          }}
                        >
                          {s.score}/10
                        </span>
                      </div>
                      <h3 className="font-display text-[15px] font-semibold text-ink leading-snug">{s.title}</h3>
                      {s.strength && (
                        <p className="text-[13px] leading-snug flex gap-1.5" style={{ color: STATUS_COLOR.green }}>
                          <TrendingUp className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span className="text-ink">{s.strength}</span>
                        </p>
                      )}
                      {s.fix ? (
                        <p className="text-[13px] leading-snug flex gap-1.5 text-ink-secondary">
                          <Wrench className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span>{s.fix}</span>
                        </p>
                      ) : s.evidence ? (
                        <p className="text-[12.5px] leading-snug text-ink-tertiary">{s.evidence}</p>
                      ) : null}
                    </div>
                    {s.locked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-surface/85 via-surface/92 to-surface/98 backdrop-blur-md">
                        <Lock className="h-4 w-4 text-ink-tertiary" />
                        <Link
                          to="/login"
                          search={loginSearch}
                          className="rounded-full border border-ink bg-ink px-3 py-1 text-[11.5px] font-medium text-sky hover:opacity-90 transition"
                        >
                          Sign in to unlock
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </section>

          {/* Final pick-your-path band */}
          <section className="mt-10 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-surface border border-line rounded-2xl p-6 flex flex-col gap-3">
              <h3 className="font-display text-[17px] font-semibold text-ink">Reach 10+ investors now</h3>
              <p className="text-[13.5px] text-ink-secondary leading-snug flex-1">
                Get your one-pager in front of 10+ thesis-matched investors this week.
              </p>
              <div>
                <Link
                  to="/apply"
                  search={{ deck: deckId }}
                  onClick={() => { try { posthog.capture("paid_cta_clicked"); } catch { /* non-fatal */ } }}
                  className="inline-flex items-center justify-center rounded-full bg-accent px-5 py-2.5 text-[14px] font-bold text-accent-ink transition hover:bg-accent-press"
                >
                  Reach investors - {pricing.submissionAmountDisplay} →
                </Link>
              </div>
            </div>
            <div className="bg-surface border border-line rounded-2xl p-6 flex flex-col gap-3">
              <h3 className="font-display text-[17px] font-semibold text-ink">Start your fundraising journey - free</h3>
              <p className="text-[13.5px] text-ink-secondary leading-snug flex-1">
                Let ikomatch run personalized investor outreach for you - every email hand-written per investor, over WhatsApp.
              </p>
              <div>
                <IkomatchCTA placement="results_footer" variant="button">
                  Start free on ikomatch →
                </IkomatchCTA>
              </div>
            </div>
          </section>

          <GrantsBanner
            sector={
              (() => {
                const s = (scoreRow as any)?.analysis?.deck_meta?.sector;
                return typeof s === "string" && s.trim() ? s.trim() : null;
              })()
            }
          />

          <p className="text-[12.5px] text-ink-tertiary text-center mt-4 mb-6">
            Are you an investor?{" "}
            <Link to="/for-investors" className="text-accent hover:underline">
              Get one-pagers of founders you refer →
            </Link>
          </p>


        </div>
      </main>
      )}



      {/* Sticky bottom CTA - two paths, side by side */}
      <div className="sticky bottom-0 border-t border-line bg-sky">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-4">
          <p className="font-display text-[15px] font-semibold mb-3">Ready for the room?</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Paid - primary */}
            <div className="rounded-xl border border-accent/40 bg-accent/10 p-4 flex flex-col gap-2">
              <h3 className="font-display text-[15px] font-semibold text-ink">We submit for you - 10+ investors</h3>
              <p className="text-[12.5px] leading-snug text-ink-secondary flex-1">
                A transaction advisor enters your deck and details into 10+ matched programs within 48 hours.
              </p>
              <div>
                <Link
                  to="/apply"
                  search={{ deck: deckId }}
                  onClick={() => { try { posthog.capture("paid_cta_clicked"); } catch { /* non-fatal */ } }}
                  className="inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-[#16171A] hover:bg-accent/90"
                >
                  Reach 10+ investors - {pricing.submissionAmountDisplay}
                </Link>
              </div>
            </div>
            {/* ikomatch - free, done-for-you cold outreach */}
            <div className="rounded-xl border border-accent/30 bg-surface p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-ink">Free</span>
                <h3 className="font-display text-[15px] font-semibold text-ink">Do it yourself, automated</h3>
              </div>
              <p className="text-[12.5px] leading-snug text-ink-secondary flex-1">
                ikomatch writes a personalized cold email per investor and sends it for you, free, run from WhatsApp.
              </p>
              <div>
                <IkomatchCTA
                  placement="results_secondary"
                  variant="unstyled"
                  className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-line bg-transparent px-5 py-2 text-[13px] font-semibold text-ink hover:bg-surface transition"
                >
                  Start free investor outreach →
                </IkomatchCTA>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Lightbox modal */}
      {activeSlide && lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-ink/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-[1200px] max-h-full bg-bg border border-line rounded-3xl shadow-xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-surface border border-line grid place-items-center text-ink-secondary hover:text-ink hover:border-ink transition"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="grid md:grid-cols-[1.35fr_1fr] gap-0 overflow-hidden">
              {/* LEFT - big slide */}
              <div className="relative bg-peach p-5 md:p-7 flex items-center justify-center min-h-[280px] md:min-h-[560px]">
                {lightboxImg ? (
                  <img
                    src={lightboxImg}
                    alt={`Slide ${activeSlide.n} - ${activeSlide.title}`}
                    className="max-w-full max-h-[70vh] object-contain rounded-xl border border-line bg-surface shadow-sm"
                  />
                ) : (
                  <div className="w-full aspect-[16/10] max-w-full rounded-xl border border-line bg-surface-sunken animate-pulse" />
                )}
                {lightboxLoading && (
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-surface/90 border border-line px-3 py-1 text-[11.5px] font-mono text-ink-tertiary">
                    rendering…
                  </span>
                )}
              </div>

              {/* RIGHT - analysis */}
              <div className="p-6 md:p-8 flex flex-col gap-4 overflow-y-auto max-h-[70vh]">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full inline-block"
                      style={{ backgroundColor: STATUS_COLOR[activeSlide.status] }}
                    />
                    <span className="font-mono text-[12px] text-ink-tertiary">
                      Slide {String(activeSlide.n).padStart(2, "0")} · {currentUnlockedPos + 1} of {unlockedIndexes.length}
                    </span>
                  </div>
                  <span
                    className="font-mono text-[12px] px-2.5 py-0.5 rounded-full border"
                    style={{
                      color: STATUS_COLOR[activeSlide.status],
                      borderColor: `color-mix(in oklab, ${STATUS_COLOR[activeSlide.status]} 40%, transparent)`,
                      backgroundColor: `color-mix(in oklab, ${STATUS_COLOR[activeSlide.status]} 12%, transparent)`,
                    }}
                  >
                    {activeSlide.score}/10
                  </span>
                </div>

                <h3 className="font-display text-[22px] font-semibold text-ink leading-tight">
                  {activeSlide.title}
                </h3>

                {/* Sub-scores */}
                <div className="flex flex-wrap gap-1.5">
                  {(
                    [
                      ["Clarity", activeSlide.subScores.clarity],
                      ["Substance", activeSlide.subScores.substance],
                      ["Relevance", activeSlide.subScores.relevance],
                      ["Credibility", activeSlide.subScores.credibility],
                      ["Design", activeSlide.subScores.design],
                    ] as const
                  ).map(([label, val]) => (
                    <span
                      key={label}
                      className="text-[11.5px] rounded-full bg-surface-sunken border border-line px-2 py-0.5 text-ink-secondary"
                    >
                      {label} <span className="font-mono text-ink">{val}/2</span>
                    </span>
                  ))}
                </div>

                {activeSlide.evidence && (
                  <p className="text-[13px] text-ink-tertiary leading-relaxed">
                    {activeSlide.evidence}
                  </p>
                )}

                {activeSlide.strength && (
                  <p
                    className="text-[13.5px] leading-snug flex gap-2"
                    style={{ color: STATUS_COLOR.green }}
                  >
                    <TrendingUp className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-ink">{activeSlide.strength}</span>
                  </p>
                )}

                {activeSlide.fix && (
                  <p className="text-[13.5px] leading-snug flex gap-2 text-ink-secondary">
                    <Wrench
                      className="h-4 w-4 shrink-0 mt-0.5"
                      style={{ color: STATUS_COLOR.amber }}
                    />
                    <span className="text-ink">{activeSlide.fix}</span>
                  </p>
                )}

                {(activeSlide.rewriteHeadline || activeSlide.rewriteBody.length > 0) && (
                  <div className="mt-1 rounded-2xl border border-line bg-sky/60 p-4">
                    <p className="uppercase text-[11px] font-semibold text-ink-tertiary tracking-wide mb-2">
                      Suggested rewrite
                    </p>
                    {activeSlide.rewriteHeadline && (
                      <p className="font-display text-[15px] font-semibold text-ink leading-snug">
                        {activeSlide.rewriteHeadline}
                      </p>
                    )}
                    {activeSlide.rewriteBody.length > 0 && (
                      <ul className="mt-2 list-disc pl-5 space-y-1 text-[13px] text-ink">
                        {activeSlide.rewriteBody.map((b, idx) => (
                          <li key={idx}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Prev / Next */}
                <div className="mt-auto pt-4 flex items-center justify-between border-t border-line">
                  <button
                    type="button"
                    onClick={gotoPrev}
                    disabled={!hasPrev}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] text-ink hover:border-ink transition disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </button>
                  <button
                    type="button"
                    onClick={gotoNext}
                    disabled={!hasNext}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] text-ink hover:border-ink transition disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GrantsBanner({ sector }: { sector: string | null }) {
  const [dismissed, setDismissed] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [matched, setMatched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let n: number | null = null;
        let didMatch = false;
        if (sector) {
          const { count: c } = await supabase
            .from("grants_public")
            .select("id", { count: "exact", head: true })
            .in("status", ["open", "upcoming"])
            .ilike("sector_focus", `%${sector}%`);
          if (typeof c === "number" && c > 0) {
            n = c;
            didMatch = true;
          }
        }
        if (n === null) {
          const { count: c } = await supabase
            .from("grants_public")
            .select("id", { count: "exact", head: true })
            .in("status", ["open", "upcoming"]);
          n = c ?? 0;
        }
        if (!cancelled) {
          setCount(n);
          setMatched(didMatch);
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sector]);

  if (dismissed || !count || count <= 0) return null;
  const label = matched ? `${count} matching grants` : `${count} live grants`;

  return (
    <div className="mt-6 mb-4 flex items-center gap-3 rounded-full border border-line bg-surface px-4 py-2 text-[13px] text-ink-secondary">
      <span className="flex-1 truncate">
        Your deck just became grant-ready.{" "}
        <Link
          to="/grants"
          search={matched && sector ? { sector } : {}}
          className="text-accent hover:underline font-medium"
        >
          Auto-apply to {label} →
        </Link>
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="text-ink-tertiary hover:text-ink text-[16px] leading-none px-1"
      >
        ×
      </button>
    </div>
  );
}
